/**
 * Google Analytics integration using react-ga4
 * Mirrors legacy dataProtect ReactGA usage with the same event categories,
 * actions, labels, and custom dimensions.
 */
import config from '@/config';

import ReactGA from 'react-ga4';

let initialized = false;

/**
 * Initialize Google Analytics with the configured tracking ID.
 * Safe to call multiple times — only initializes once.
 */
export const initAnalytics = () => {
  const trackingId = config.GA_TRACKING_ID;
  if (!trackingId || initialized) return;

  ReactGA.initialize(trackingId, {
    gaOptions: { titleCase: false },
  });
  initialized = true;
};

/**
 * Track a login event with user identity.
 * Matches legacy: eventCategory "User", eventAction "Login"
 */
export const trackLogin = (email: string, userId: string) => {
  if (!initialized) return;
  ReactGA.event({
    category: 'User',
    action: 'Login',
    label: `${email} logged in.`,
  });
  ReactGA.set({ dimension1: userId });
};

/**
 * Track a logout event with user identity.
 * Matches legacy: eventCategory "User", eventAction "Logout"
 */
export const trackLogout = (email: string, userId: string) => {
  if (!initialized) return;
  ReactGA.event({
    category: 'User',
    action: 'Logout',
    label: `${email} logged out.`,
  });
  ReactGA.set({ dimension1: userId });
};

/**
 * Track entrance analytics based on URL parameters.
 * Matches legacy dataProtect trackEntranceAnalytics().
 */
export const trackEntranceAnalytics = (params: {
  template?: string;
  subscriber?: string;
  access?: string;
  invitationKey?: string;
}) => {
  if (!initialized) return;

  if (params.template) {
    ReactGA.event({
      category: 'User',
      action: 'Visit',
      label: 'User has visited app from templates.',
    });
  }
  if (params.subscriber) {
    ReactGA.event({
      category: 'User',
      action: 'Visit',
      label: 'First visit by new subscriber.',
    });
  }
  if (params.access) {
    ReactGA.event({
      category: 'User',
      action: 'Visit',
      label: 'New user has visited app, from free trial form.',
    });
  }
  if (params.invitationKey) {
    ReactGA.event({
      category: 'User',
      action: 'Visit',
      label: 'New potential user has visited app, with invitation code.',
    });
  }
};

/**
 * Track a quick action click.
 * Matches legacy: eventCategory "QuickActions", eventAction "Click"
 */
export const trackQuickAction = (actionName: string, tenantKey?: string, appKey?: string) => {
  if (!initialized) return;
  ReactGA.event({
    category: 'QuickActions',
    action: 'Click',
    label: `Quick Action: ${actionName}`,
  });
  if (tenantKey) ReactGA.set({ dimension1: tenantKey });
  if (appKey) ReactGA.set({ dimension2: appKey });
};

/**
 * Track a recipe execution result.
 * Matches legacy: eventCategory "Recipes", eventAction "Execution Error" / "Execution Succeeded"
 */
export const trackRecipeExecution = (params: {
  success: boolean;
  recipeId: string;
  recipeName: string;
  elapsedSeconds: number;
  error?: string;
  userId?: string;
  tenantKey?: string;
  appKey?: string;
}) => {
  if (!initialized) return;

  const label = params.success
    ? `${params.recipeId}, ${params.recipeName}, took ${params.elapsedSeconds} seconds.`
    : `${params.recipeId}, ${params.recipeName}, ${params.error || 'Unknown error'}, took ${params.elapsedSeconds} seconds.`;

  ReactGA.event({
    category: 'Recipes',
    action: params.success ? 'Execution Succeeded' : 'Execution Error',
    label,
  });

  if (params.userId) ReactGA.set({ dimension1: params.userId });
  if (params.tenantKey) ReactGA.set({ dimension2: params.tenantKey });
  if (params.appKey) ReactGA.set({ dimension3: params.appKey });
};

/**
 * Send a page view. Call on route changes if needed.
 */
export const trackPageView = (path: string, title?: string) => {
  if (!initialized) return;
  ReactGA.send({ hitType: 'pageview', page: path, title });
};
